
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, isOnline } from "@/integrations/supabase/client";
import { Expense } from "@/types/expense";
import { Settlement } from "@/types/payment";
import { useGroupMembers } from "./useGroupMembers";
import { useGroupExpenses } from "./useGroupExpenses";
import { useGroupSettlements } from "./useGroupSettlements";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";

export const useGroupPage = (groupId: string | undefined) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isMounted = useRef(true);
  
  // State for group details
  const [groupName, setGroupName] = useState<string>("");
  const [groupStatus, setGroupStatus] = useState<string>("active");
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [settlementCounter, setSettlementCounter] = useState(0);
  const initialFetchDone = useRef(false);
  
  // Optimized handler for settlement updates
  const handleSettlementUpdate = useCallback(() => {
    if (isMounted.current) {
      setSettlementCounter(prev => prev + 1);
    }
  }, []);

  // Initialize hooks
  const {
    members,
    showAddMember,
    showShareLink,
    setShowAddMember,
    setShowShareLink,
    fetchMembers,
    handleRemoveMember
  } = useGroupMembers(groupId);

  const {
    expenses,
    showExpenseForm,
    editingExpenseId,
    expenseFormState,
    setShowExpenseForm,
    fetchExpenses,
    handleExpenseSubmit,
    handleEditExpense,
    handleDeleteExpense
  } = useGroupExpenses(groupId, handleSettlementUpdate);

  const {
    settlements,
    fetchSettlements
  } = useGroupSettlements(groupId, expenses, settlementCounter);

  // Initial data loading
  useEffect(() => {
    if (groupId && !initialFetchDone.current && isMounted.current) {
      console.log("Initial data loading in useGroupPage");
      
      const loadInitialData = async () => {
        try {
          // Try to fetch initial data
          await Promise.all([
            fetchMembers(),
            fetchExpenses()
          ]);
          
          initialFetchDone.current = true;
        } catch (error) {
          console.error("Error loading initial group data:", error);
        }
      };
      
      loadInitialData();
    }
  }, [groupId, fetchMembers, fetchExpenses]);

  // Set loading state based on data availability
  useEffect(() => {
    if (isMounted.current) {
      if (groupId && groupName) {
        setLoading(false);
      } else {
        setLoading(true);
      }
    }
  }, [groupId, groupName]);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
      initialFetchDone.current = false;
    };
  }, []);

  return {
    // Group info
    groupName,
    groupStatus,
    loading,
    session,
    
    // Members
    members,
    showAddMember,
    showShareLink,
    setShowAddMember,
    setShowShareLink,
    handleRemoveMember,
    
    // Expenses
    expenses,
    showExpenseForm,
    editingExpenseId,
    expenseFormState,
    setShowExpenseForm,
    handleExpenseSubmit,
    handleEditExpense,
    handleDeleteExpense,
    
    // Settlements
    settlements,
    handleSettlementUpdate,
    
    // Setters
    setGroupName,
    setGroupStatus,
    setSession,
    
    // Functions
    fetchMembers,
    fetchExpenses,
    fetchSettlements
  };
};
